/**
 * Agent activity — query hooks.
 *
 * Read-only. The aggregate is computed SERVER-SIDE over the tamper-evident evidence chain + the
 * cumulative ledger. Backed by agentactivity:
 * GET /api/v1/tenants/{tenant_id}/agents/agent-activity?pivot&id&days&bucket.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import {
  agentActivitySchema,
  type AgentActivity,
  type ActivityBucket,
  type ActivityPivot,
  type AgentKind,
  type RiskTier,
  type AutonomyTier,
} from "./schemas";

export const DEFAULT_WINDOW_DAYS = 30;
export const DEFAULT_PIVOT: ActivityPivot = "agent";

export interface ActivityParams {
  pivot: ActivityPivot;
  id?: string;
  days: number;
  bucket?: ActivityBucket;
  // "compact" is the embedded per-subject projection: the server returns only summary + rows (+ caps
  // for a focused agent + the name maps), skipping the tenant-analyst extras. Used by the user-detail
  // and agent-detail activity tabs so a detail page doesn't pay the full aggregation.
  // "trend" is the Trends sub-tab's projection: compact-like + ONLY the decisions timeseries, so the
  // trend chart doesn't trigger the full leaderboard/value/reason/comparison aggregation.
  view?: "compact" | "trend";
  // Segment filter (agent-registry attributes) — re-scopes the whole page to matching agents.
  kind?: AgentKind;
  risk_tier?: RiskTier;
  autonomy?: AutonomyTier;
}

function endpoint(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/agents/agent-activity`;
}

export function activityQueryKey(tenantID: string, p: ActivityParams) {
  return [
    "agents", "agent-activity", tenantID, p.pivot, p.id ?? "", p.days, p.bucket ?? "day", p.view ?? "full",
    p.kind ?? "", p.risk_tier ?? "", p.autonomy ?? "",
  ] as const;
}

export async function fetchAgentActivity(tenantID: string, p: ActivityParams): Promise<AgentActivity> {
  const searchParams: Record<string, string> = { pivot: p.pivot, days: String(p.days) };
  if (p.id) searchParams.id = p.id;
  if (p.bucket) searchParams.bucket = p.bucket;
  if (p.view) searchParams.view = p.view;
  if (p.kind) searchParams.kind = p.kind;
  if (p.risk_tier) searchParams.risk_tier = p.risk_tier;
  if (p.autonomy) searchParams.autonomy = p.autonomy;
  const raw = await apiFetch<unknown>(endpoint(tenantID), { searchParams });
  return agentActivitySchema.parse(raw);
}

export function useAgentActivityQuery(tenantID: string, p: ActivityParams, options?: { enabled?: boolean }) {
  return useQuery<AgentActivity, ApiError>({
    queryKey: activityQueryKey(tenantID, p),
    queryFn: () => fetchAgentActivity(tenantID, p),
    enabled: tenantID.length > 0 && (options?.enabled ?? true),
    // Governance reporting, not a live feed. A modest stale-time keeps pivot/window toggles snappy;
    // keepPreviousData avoids a full spinner when only the lens rotates.
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    // Polling cadence keys off the fleet cap surface:
    //  - cold rollup computing (pending) → fast 2.5s poll so it resolves promptly;
    //  - a served CACHED rollup (has refreshed_at) → slow 60s governance poll, so its "Updated Ns ago"
    //    label doesn't silently age and each read past the TTL drives the background refresh;
    //  - anything else (a LIVE segmented scan — no refreshed_at, computed each fetch — or a focused/
    //    by-user view with no distribution, or an unavailable surface) → NO auto-poll. This is what
    //    stops a segmented view (which bypasses the rollup and scans live) from re-running its
    //    per-request scan every minute on an idle tab.
    refetchInterval: (query) => {
      const cd = query.state.data?.cap_distribution;
      const tb = query.state.data?.take_back;
      // Fast-poll while EITHER materialized surface is cold+computing (the take-back drives this on the
      // user pivot, which has no cap distribution). Slow governance poll while either is a served rollup.
      if (cd?.pending || tb?.pending) return 2_500;
      if (cd?.refreshed_at || tb?.refreshed_at) return 60_000;
      return false;
    },
  });
}
